/**
 * The user lookup person card, grouped (8 Oct): Identity, Contact, Work, Ivanti.
 *
 * "Add customer ID and mobile, as many fields as you can." The fields were
 * chosen from a read-only census of the LIVE Employee object (every
 * meaningful field set on 5 % or more of active staff, plus the ones IT fills
 * by hand). Pure: no React, no i18n — the page passes its formatters in, so
 * the grouping and the empty rule are unit tested in Node.
 */

import type { ProfilePerson } from "@/lib/api";

export type PersonSectionKey = "identity" | "contact" | "work" | "ivanti";

export type PersonFieldKey =
  | "customerId"
  | "firstName"
  | "lastName"
  | "login"
  | "upn"
  | "entraObjectId"
  | "status"
  | "leaver"
  | "entraDeleted"
  | "terminatedDate"
  | "email"
  | "mobile"
  | "phone"
  | "workPhone"
  | "extension"
  | "title"
  | "department"
  | "organisation"
  | "businessUnit"
  | "costCentre"
  | "location"
  | "office"
  | "manager"
  | "director"
  | "team"
  | "teamEmail"
  | "projectRole"
  | "vip"
  | "roles"
  | "namedLicence"
  | "chatAnalyst"
  | "signIn"
  | "autoProvisioned"
  | "created"
  | "modified"
  | "source";

export interface PersonRow {
  key: PersonFieldKey;
  /** The text to show; null shows the empty mark ("—"). */
  value: string | null;
  /** Show a Copy button (user ID, email, phones). */
  copy?: boolean;
  /** An in-app link for the value (the manager's own profile). */
  href?: string;
}

export interface PersonSection {
  key: PersonSectionKey;
  rows: PersonRow[];
}

export interface PersonFormatters {
  /** A yes/no flag. */
  flag: (value: boolean) => string;
  /** An ISO timestamp as a date. */
  date: (iso: string) => string;
  /** Which leaver flags are set: "disabled", "terminated", "entraDeleted". */
  leaver: (flags: ("disabled" | "terminated" | "entraDeleted")[]) => string;
  /** How they sign in: Entra single sign-on, an Ivanti password, or both. */
  signIn: (external: boolean, internal: boolean) => string;
}

function text(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

function flag(value: boolean | null | undefined, fmt: PersonFormatters): string | null {
  return value == null ? null : fmt.flag(value);
}

function joined(parts: (string | null | undefined)[], separator: string): string | null {
  return text(
    parts
      .map(text)
      .filter((part): part is string => part !== null)
      .join(separator),
  );
}

/** "IT (1210-13)": a value with its code in brackets when both exist. */
function withCode(
  value: string | null | undefined,
  code: string | null | undefined,
): string | null {
  const main = text(value);
  const extra = text(code);
  if (main === null) {
    return extra;
  }
  return extra === null ? main : `${main} (${extra})`;
}

/** "2026-07-31 · InternalServices": a date with who, when either exists. */
function stamp(
  iso: string | null | undefined,
  who: string | null | undefined,
  fmt: PersonFormatters,
): string | null {
  const when = text(iso);
  return joined([when === null ? null : fmt.date(when), who], " · ");
}

function leaverValue(person: ProfilePerson, fmt: PersonFormatters): string | null {
  const known = [person.disabled, person.terminated, person.entra_deleted].some((v) => v != null);
  if (!known) {
    return null;
  }
  const set: ("disabled" | "terminated" | "entraDeleted")[] = [];
  if (person.disabled === true) {
    set.push("disabled");
  }
  if (person.terminated === true) {
    set.push("terminated");
  }
  if (person.entra_deleted === true) {
    set.push("entraDeleted");
  }
  return fmt.leaver(set);
}

function signInValue(person: ProfilePerson, fmt: PersonFormatters): string | null {
  if (person.external_auth == null && person.internal_auth == null) {
    return null;
  }
  return fmt.signIn(person.external_auth === true, person.internal_auth === true);
}

function namedLicence(person: ProfilePerson, fmt: PersonFormatters): string | null {
  const bundle = text(person.licence_bundle);
  if (person.named_user === true) {
    return bundle ?? fmt.flag(true);
  }
  return bundle ?? flag(person.named_user, fmt);
}

function chatAnalyst(person: ProfilePerson, fmt: PersonFormatters): string | null {
  const department = text(person.chat_department);
  if (person.chat_analyst === true && department !== null) {
    return `${fmt.flag(true)} (${department})`;
  }
  return flag(person.chat_analyst, fmt);
}

/** The profile page's link to a person. */
export function personHref(employeeId: string): string {
  return `/it-admin/people/${encodeURIComponent(employeeId)}`;
}

/** The four sections of the person card, every row present (empty ones too). */
export function personSections(person: ProfilePerson, fmt: PersonFormatters): PersonSection[] {
  const managerId = text(person.manager_id);
  const manager = text(person.manager) ?? text(person.manager_email);
  return [
    {
      key: "identity",
      rows: [
        { key: "customerId", value: text(person.customer_id), copy: true },
        { key: "firstName", value: text(person.first_name) },
        { key: "lastName", value: text(person.last_name) },
        { key: "login", value: text(person.login_id) },
        { key: "upn", value: text(person.network_user_name) },
        { key: "entraObjectId", value: text(person.entra_object_id) },
        { key: "status", value: text(person.status) },
        { key: "leaver", value: leaverValue(person, fmt) },
        { key: "entraDeleted", value: flag(person.entra_deleted, fmt) },
        { key: "terminatedDate", value: text(person.terminated_date) },
      ],
    },
    {
      key: "contact",
      rows: [
        { key: "email", value: text(person.email), copy: true },
        { key: "mobile", value: text(person.mobile), copy: true },
        { key: "phone", value: text(person.phone), copy: true },
        { key: "workPhone", value: text(person.work_phone), copy: true },
        { key: "extension", value: joined([person.phone_ext, person.work_phone_ext], " / ") },
      ],
    },
    {
      key: "work",
      rows: [
        { key: "title", value: text(person.title) },
        { key: "department", value: withCode(person.department, person.department_code) },
        { key: "organisation", value: text(person.organisation) },
        { key: "businessUnit", value: text(person.business_unit) },
        { key: "costCentre", value: text(person.cost_centre) },
        { key: "location", value: joined([person.location, person.country], ", ") },
        { key: "office", value: text(person.office) },
        {
          key: "manager",
          value: manager,
          ...(manager !== null && managerId !== null ? { href: personHref(managerId) } : {}),
        },
        { key: "director", value: text(person.director) },
        { key: "team", value: text(person.team) },
        { key: "teamEmail", value: text(person.team_email) },
        { key: "projectRole", value: text(person.project_role) },
        { key: "vip", value: flag(person.vip, fmt) },
      ],
    },
    {
      key: "ivanti",
      rows: [
        { key: "roles", value: joined(person.roles ?? [], ", ") },
        { key: "namedLicence", value: namedLicence(person, fmt) },
        { key: "chatAnalyst", value: chatAnalyst(person, fmt) },
        { key: "signIn", value: signInValue(person, fmt) },
        { key: "autoProvisioned", value: flag(person.auto_provisioned, fmt) },
        { key: "created", value: stamp(person.created_at, person.created_by, fmt) },
        { key: "modified", value: stamp(person.modified_at, person.modified_by, fmt) },
        { key: "source", value: text(person.creation_source) },
      ],
    },
  ];
}
